import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Observable } from 'rxjs/Observable';
import { MatchingAgency } from '../../event/models/matching-agency';
import { UserLocalStorageService } from '../../permission/services/user.local.storage.service';
import { Constants } from '../constants';
import { PagesizeType } from '../../event/service/pagination/pagesize-type';
import { MatchingAgencyStore } from '../../event/service/matching-agency.store';
import { MatchingAgencyField } from '../../event/models/matching-agency-field';
import { Page } from '../models/page';
import { EventAgencyStatus } from '../models/event-agency-status.enum';
import { MatchingAgencyCandidateAssign } from '../../event/models/matching-agency-candidate-assign';
import { MatchingAgencyAdd } from '../../event/models/matching-agency-add';
import { catchError, map } from 'rxjs/operators';
import { AgencyLookupDetail } from '../models/agency-lookup-detail';
import { of } from 'rxjs/observable/of';
import { MatchingAgencyHttpService } from '../../event/service/matching-agency.http.service';
import { LogService } from '../log/log.service';

@Injectable()
export class MatchingAgencyService {

  private selectedAgency: BehaviorSubject<MatchingAgency> = new BehaviorSubject<MatchingAgency>(null);

  private selectedSortField: BehaviorSubject<{field: string, direction: string}>
    = new BehaviorSubject<{field: string,
          direction: string}>({field: Constants.DEFAULT_MATCHING_AGENCY_FIELD_SORT_FIELD,  direction: Constants.SORT_DIRECTION_ASC});

  private selectedPage: BehaviorSubject<number> = new BehaviorSubject<number>(null);

  constructor(
    private matchingAgencyStore: MatchingAgencyStore,
    private userLocalStorageService: UserLocalStorageService,
    private matchingAgencyHttpService: MatchingAgencyHttpService,
    private logService: LogService
  ) {
    this.init();
  }

  protected init(): void {
    this.setDefaultSort();
    this.setDefaultPage();
  }

  private setDefaultSort() {
    this.selectedSortField.next({field: Constants.DEFAULT_MATCHING_AGENCY_FIELD_SORT_FIELD,  direction: Constants.SORT_DIRECTION_ASC});
  }

  private setDefaultPage() {
    this.selectedPage.next(Constants.SEARCH_DEFAULT_PAGE);
  }

  get agencies(): Observable<Page<MatchingAgency>> {
    return this.matchingAgencyStore.matches;
  }

  get totalAgencyCount(): Observable<number> {
    return this.matchingAgencyStore.count;
  }

  get selectedAgency$(): Observable<MatchingAgency> {
    return this.selectedAgency.asObservable();
  }

  get selectedSortField$(): Observable<{field: string, direction: string}> {
    return this.selectedSortField.asObservable();
  }

  get selectedPage$(): Observable<number> {
    return this.selectedPage.asObservable();
  }

  public setSelectedAgency(agency: MatchingAgency) {
    this.selectedAgency.next(agency);
  }

  public resetSelectedAgency(): void {
    this.selectedAgency.next(null);
  }

  public setSelectedSortField(field_: string, direction_: string) {
    this.selectedSortField.next({field: field_, direction: direction_});
  }

  public resetSelectedSortField(): void {
    this.setDefaultSort();
  }

  public setSelectedPage(page: number) {
    this.selectedPage.next(page);
  }

  public resetSelectedPage(): void {
    this.setDefaultPage();
  }

  public assignMatchingCandidate(candidateAssign: MatchingAgencyCandidateAssign): Observable<any> {
    return this.matchingAgencyStore.assignCandidate(candidateAssign);
  }

  public refreshMatchingAgencies(page: number, field?: string, direction?: string): Observable<any> {
    this.setSelectedPage(page);

    let selectedField = field ? field : Constants.DEFAULT_MATCHING_AGENCY_FIELD_SORT_FIELD;
    let selectedDirection = direction ? direction : Constants.SORT_DIRECTION_ASC;
    this.setSelectedSortField(selectedField, selectedDirection);

    return this.matchingAgencyStore.getMatches(
      page,
      this.userLocalStorageService.getPagesize(PagesizeType.MATCHING_AGENCY),
      selectedField,
      this.getSecondarySource(selectedField),
      selectedDirection
    );
  }

  public addAgency(newRec: MatchingAgencyAdd): Observable<MatchingAgency> {
    return this.matchingAgencyStore.addAgency(newRec);
  }

  private getSecondarySource(field: string): string {
    if (field === MatchingAgencyField.CREATED_DATE) {
      return MatchingAgencyField.PROPERTY_NAME;
    } else if (field === MatchingAgencyField.PROPERTY_NAME) {
      return MatchingAgencyField.CREATED_DATE;
    } else if (field === MatchingAgencyField.PROPERTY_ID) {
      return MatchingAgencyField.CREATED_DATE;
    } else {
      return MatchingAgencyField.MATCHING_AGENCY_ID;
    }
  }

  public matchingAgencyProcess(model: any, runMatchingAgency: boolean) {
    if (runMatchingAgency) {
      return this.matchingAgencyHttpService.search(model.eventAgency.code, model.eventProperty.chain).pipe(
        map((response: AgencyLookupDetail) => {
          if (response) {
            this.logService.debug('matchingAgencyProcess - Agency match found, mark Event Agency as MAPPED');
            model.eventAgency.code = response.agencyId;
            model.eventAgency.subscriber = response.subscriber;
            model.eventAgency.eventAgencyStatus = EventAgencyStatus.AGENCY_MAPPED;
            model.eventAgency.originalCode = response.pseudoAgencyId ? response.pseudoAgencyId : null;
          } else {
            this.logService.debug('matchingAgencyProcess - Agency match NOT found, mark Event Agency as CREATE NEW');
            model.eventAgency.eventAgencyStatus = EventAgencyStatus.CREATE_NEW_AGENCY;
          }
          return model;
        }),
        catchError((error) => {
          this.logService.error(`matchingAgencyProcess - Agency match returned an error, mark Event Agency as CREATE NEW: ${error}`);
          // model is returned without changes
          model.eventAgency.eventAgencyStatus = EventAgencyStatus.CREATE_NEW_AGENCY;
          return of(model);
        })
      );
    } else {
      model.eventAgency.eventAgencyStatus = model.eventAgency && model.eventAgency.newTravelAgency ? EventAgencyStatus.CREATE_NEW_AGENCY
        : EventAgencyStatus.NONE;
      return of(model);
    }
  }

}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/qfGQY7pgg/1/oneonyx/grouppay-group/grouppay/pioneer-web/src/app/shared/matching-agency/matching-agency.service.ts